/**
 * Outline geometry for marked shapes.
 *
 * Bands are stored as polygons (their border), so every edge is editable by
 * its vertices. This module builds those outlines, draws them with rounded
 * corners, and merges overlapping shapes into one (raster union → contour).
 */
import type { Shape, Vec } from '../../shared/doc.ts';
import { inflate, nearestOnSegment, polyArea, polyBounds, polylineLength, rectsOverlap, simplify, type Rect } from './geom.ts';
import { flattenPath } from './curve.ts';

/** Offset a polyline sideways by d (left of travel for d > 0), one point per input point. */
export function offsetSide(pts: number[], d: number): number[] {
  const n = pts.length / 2;
  const out: number[] = [];
  if (n === 0) return out;
  if (n === 1) return [pts[0]!, pts[1]!];
  for (let i = 0; i < n; i++) {
    const p = Math.max(0, i - 1),
      q = Math.min(n - 1, i + 1);
    // normals of the two adjacent segments, averaged (miter), limited to 2× the offset
    const ax = pts[2 * i]! - pts[2 * p]!,
      ay = pts[2 * i + 1]! - pts[2 * p + 1]!;
    const bx = pts[2 * q]! - pts[2 * i]!,
      by = pts[2 * q + 1]! - pts[2 * i + 1]!;
    const al = Math.hypot(ax, ay) || 0,
      bl = Math.hypot(bx, by) || 0;
    let nx = 0,
      ny = 0;
    if (al > 0) {
      nx += -ay / al;
      ny += ax / al;
    }
    if (bl > 0) {
      nx += -by / bl;
      ny += bx / bl;
    }
    const nl = Math.hypot(nx, ny);
    if (nl < 1e-6) {
      // a 180° turn: fall back to the incoming normal
      const l = al || bl || 1;
      const sx = al ? ax : bx,
        sy = al ? ay : by;
      out.push(pts[2 * i]! + (-sy / l) * d, pts[2 * i + 1]! + (sx / l) * d);
      continue;
    }
    nx /= nl;
    ny /= nl;
    // at a corner both segments contribute and |na + nb| = 2·cos(θ/2): the miter
    // length is 1 / cos(θ/2), capped at 2; at an endpoint the normal is already unit
    const both = al > 0 && bl > 0;
    const scale = both ? Math.min(2, 2 / Math.max(1e-3, nl)) : 1;
    out.push(pts[2 * i]! + nx * d * scale, pts[2 * i + 1]! + ny * d * scale);
  }
  return out;
}

/** Closed outline of a band of constant width around a polyline (flat ends). */
export function bandOutline(mid: number[], width: number): number[] {
  if (mid.length < 4) return [];
  const l = offsetSide(mid, width / 2);
  const r = offsetSide(mid, -width / 2);
  return joinSides(l, r);
}

/** Closed polygon from a left side and a right side that run the same way. */
export function joinSides(left: number[], right: number[]): number[] {
  const out = left.slice();
  for (let i = right.length - 2; i >= 0; i -= 2) out.push(right[i]!, right[i + 1]!);
  return out;
}

/**
 * The vertices a person would want to grab: the corners of the shape. A smooth
 * side (a band following a curved bank) is made of many small turns that are
 * not corners; a simple shape (up to 12 vertices) shows every vertex.
 */
export function keyVertices(pts: number[], closed: boolean, reach: number, minTurnDeg = 35): number[] {
  const n = pts.length / 2;
  if (n <= 12) return Array.from({ length: n }, (_, i) => i);
  const P = (i: number): Vec => ({ x: pts[2 * (((i % n) + n) % n)]!, y: pts[2 * (((i % n) + n) % n) + 1]! });
  // the point at least `reach` px away along the polygon in each direction
  const away = (i: number, dir: 1 | -1): Vec => {
    let acc = 0,
      k = i;
    for (let step = 0; step < n - 1; step++) {
      const j = k + dir;
      if (!closed && (j < 0 || j >= n)) break;
      acc += Math.hypot(P(j).x - P(k).x, P(j).y - P(k).y);
      k = j;
      if (acc >= reach) break;
    }
    return P(k);
  };
  const minCos = Math.cos((minTurnDeg * Math.PI) / 180);
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    if (!closed && (i === 0 || i === n - 1)) {
      out.push(i);
      continue;
    }
    const a = away(i, -1),
      b = away(i, 1),
      p = P(i);
    const ux = p.x - a.x,
      uy = p.y - a.y,
      vx = b.x - p.x,
      vy = b.y - p.y;
    const ul = Math.hypot(ux, uy) || 1,
      vl = Math.hypot(vx, vy) || 1;
    if ((ux * vx + uy * vy) / (ul * vl) <= minCos) out.push(i);
  }
  return out;
}

/** Any shape as a closed polygon (legacy bands become their outline). Its border only (a mark's cuts: cuts.ts). */
export function shapeToPolygon(s: Shape): number[] {
  if (s.k === 'poly') return s.pts;
  if (s.k === 'path') return flattenPath(s.pts, s.h);
  if (s.k === 'rect') return [s.x, s.y, s.x + s.w, s.y, s.x + s.w, s.y + s.h, s.x, s.y + s.h];
  return bandOutline(s.pts, s.width);
}

/**
 * Path of a polygon with rounded corners: each corner is cut by up to `r`
 * along both edges (never more than half an edge) and joined by an arc.
 */
export function roundedPolygonPath(ctx: CanvasRenderingContext2D, pts: number[], r: number, begin = true): void {
  const n = pts.length / 2;
  if (n < 3 || r <= 0) {
    if (begin) ctx.beginPath();
    for (let i = 0; i < n; i++) {
      if (i === 0) ctx.moveTo(pts[0]!, pts[1]!);
      else ctx.lineTo(pts[2 * i]!, pts[2 * i + 1]!);
    }
    ctx.closePath();
    return;
  }
  const P = (i: number): Vec => ({ x: pts[2 * ((i + n) % n)]!, y: pts[2 * ((i + n) % n) + 1]! });
  if (begin) ctx.beginPath();
  // start at the midpoint of the last edge so every corner is handled uniformly
  const p0 = P(n - 1),
    p1 = P(0);
  ctx.moveTo((p0.x + p1.x) / 2, (p0.y + p1.y) / 2);
  for (let i = 0; i < n; i++) {
    const a = P(i - 1),
      b = P(i),
      c = P(i + 1);
    const la = Math.hypot(b.x - a.x, b.y - a.y),
      lb = Math.hypot(c.x - b.x, c.y - b.y);
    const ri = Math.min(r, la / 2, lb / 2);
    if (ri < 0.2) ctx.lineTo(b.x, b.y);
    else ctx.arcTo(b.x, b.y, c.x, c.y, ri);
  }
  ctx.closePath();
}

/** Centroid if it lies inside the polygon, else the outline point nearest to it (`anywhere`: the centroid wherever it lies). */
export function anchorInside(pts: number[], anywhere = false): Vec {
  const n = pts.length / 2;
  let cx = 0,
    cy = 0,
    area = 0;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const cross = pts[2 * i]! * pts[2 * j + 1]! - pts[2 * j]! * pts[2 * i + 1]!;
    area += cross;
    cx += (pts[2 * i]! + pts[2 * j]!) * cross;
    cy += (pts[2 * i + 1]! + pts[2 * j + 1]!) * cross;
  }
  if (Math.abs(area) < 1e-6) return { x: pts[0] ?? 0, y: pts[1] ?? 0 };
  cx /= 3 * area;
  cy /= 3 * area;
  if (anywhere || pointInPolygon(cx, cy, pts)) return { x: cx, y: cy };
  // nearest vertex-midpoint on the outline
  let best = { x: pts[0]!, y: pts[1]! },
    bd = Infinity;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const mx = (pts[2 * i]! + pts[2 * j]!) / 2,
      my = (pts[2 * i + 1]! + pts[2 * j + 1]!) / 2;
    const d = Math.hypot(mx - cx, my - cy);
    if (d < bd) {
      bd = d;
      best = { x: mx, y: my };
    }
  }
  return best;
}

function pointInPolygon(x: number, y: number, pts: number[]): boolean {
  const n = pts.length / 2;
  let inside = false;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = pts[2 * i]!,
      yi = pts[2 * i + 1]!,
      xj = pts[2 * j]!,
      yj = pts[2 * j + 1]!;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/* ---------------------------------------------------------------- union */

interface Raster {
  x0: number;
  y0: number;
  w: number;
  h: number;
  s: number; // cells per image px
  mask: Uint8Array;
}

const MAX_CELLS = 3_000_000;

function rasterFor(bounds: Rect, cellsPerPx = 1, pad = 2): Raster {
  const bw = bounds.w + 2 * pad,
    bh = bounds.h + 2 * pad;
  let s = cellsPerPx;
  if (bw * bh * s * s > MAX_CELLS) s = Math.sqrt(MAX_CELLS / (bw * bh));
  const w = Math.max(1, Math.ceil(bw * s)),
    h = Math.max(1, Math.ceil(bh * s));
  return { x0: bounds.x - pad, y0: bounds.y - pad, w, h, s, mask: new Uint8Array(w * h) };
}

/** Scanline even-odd fill of a polygon into the raster (cell centres). */
function fillPolygon(r: Raster, pts: number[], value = 1): void {
  const n = pts.length / 2;
  if (n < 3) return;
  const xs: number[] = [];
  for (let row = 0; row < r.h; row++) {
    const y = r.y0 + (row + 0.5) / r.s;
    xs.length = 0;
    for (let i = 0, j = n - 1; i < n; j = i++) {
      const yi = pts[2 * i + 1]!,
        yj = pts[2 * j + 1]!;
      if (yi > y === yj > y) continue;
      const xi = pts[2 * i]!,
        xj = pts[2 * j]!;
      xs.push(xi + ((y - yi) * (xj - xi)) / (yj - yi));
    }
    if (xs.length < 2) continue;
    xs.sort((a, b) => a - b);
    for (let k = 0; k + 1 < xs.length; k += 2) {
      const c0 = Math.max(0, Math.ceil((xs[k]! - r.x0) * r.s - 0.5)),
        c1 = Math.min(r.w - 1, Math.floor((xs[k + 1]! - r.x0) * r.s - 0.5));
      for (let c = c0; c <= c1; c++) r.mask[row * r.w + c] = (r.mask[row * r.w + c] ?? 0) | value;
    }
  }
}

/** Outer contour of the set cells (Moore tracing), in image px (cell centres). */
function traceContour(r: Raster): number[] {
  const { w, h, mask } = r;
  const at = (x: number, y: number) => (x >= 0 && y >= 0 && x < w && y < h ? mask[y * w + x]! : 0);
  let sx = -1,
    sy = -1;
  outer: for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) if (mask[y * w + x]) {
    sx = x;
    sy = y;
    break outer;
  }
  if (sx < 0) return [];
  const N8 = [
    [-1, -1],
    [0, -1],
    [1, -1],
    [1, 0],
    [1, 1],
    [0, 1],
    [-1, 1],
    [-1, 0],
  ] as const;
  const nextFrom = (x: number, y: number, bx0: number, by0: number): { nx: number; ny: number; pbx: number; pby: number } | null => {
    let bi = 0;
    for (let i = 0; i < 8; i++) if (x + N8[i]![0]! === bx0 && y + N8[i]![1]! === by0) bi = i;
    let prevX = bx0,
      prevY = by0;
    for (let i = 1; i <= 8; i++) {
      const d = N8[(bi + i) % 8]!;
      const nx = x + d[0]!,
        ny = y + d[1]!;
      if (at(nx, ny)) return { nx, ny, pbx: prevX, pby: prevY };
      prevX = nx;
      prevY = ny;
    }
    return null;
  };
  const cells: number[] = [];
  let cx = sx,
    cy = sy,
    bx = sx - 1,
    by = sy;
  const first = nextFrom(cx, cy, bx, by);
  if (!first) return [];
  const maxPts = 6 * (w + h) + 64;
  for (let step = 0; step < maxPts; step++) {
    cells.push(cx, cy);
    const nx = nextFrom(cx, cy, bx, by);
    if (!nx) break;
    if (cx === sx && cy === sy && step > 0 && nx.nx === first.nx && nx.ny === first.ny) break;
    bx = nx.pbx;
    by = nx.pby;
    cx = nx.nx;
    cy = nx.ny;
  }
  const out: number[] = [];
  for (let i = 0; i < cells.length; i += 2) out.push(r.x0 + (cells[i]! + 0.5) / r.s, r.y0 + (cells[i + 1]! + 0.5) / r.s);
  return out;
}

/** Where a contour point lands on the input polygons: polygon, edge (k → k+1), position along it. */
interface EdgeHit {
  p: number;
  e: number;
  t: number;
  x: number;
  y: number;
}

function nearestEdge(px: number, py: number, polys: number[][], maxD: number): EdgeHit | null {
  let best: EdgeHit | null = null,
    bd = maxD;
  for (let pi = 0; pi < polys.length; pi++) {
    const poly = polys[pi]!;
    const n = poly.length / 2;
    for (let k = 0; k < n; k++) {
      const j = (k + 1) % n;
      const ax = poly[2 * k]!,
        ay = poly[2 * k + 1]!,
        cx = poly[2 * j]!,
        cy = poly[2 * j + 1]!;
      if (px < Math.min(ax, cx) - bd || px > Math.max(ax, cx) + bd || py < Math.min(ay, cy) - bd || py > Math.max(ay, cy) + bd) continue;
      const r = nearestOnSegment({ x: px, y: py }, { x: ax, y: ay }, { x: cx, y: cy });
      if (r.d < bd) {
        bd = r.d;
        best = { p: pi, e: k, t: r.t, x: r.p.x, y: r.p.y };
      }
    }
  }
  return best;
}

/**
 * The union's border rebuilt from the inputs' own geometry. The raster only
 * decides which stretch of which polygon lies on the outside; along each such
 * stretch the polygon's exact vertices are emitted (and the two points where
 * the contour crosses from one polygon to the next are pulled onto their
 * edges), so nothing of the cell staircase survives — a curved side is the
 * side itself, a nook between two marks keeps its exact apex.
 */
function rebuildOnEdges(contour: number[], polys: number[][], maxD: number): number[] {
  const n = contour.length / 2;
  if (n === 0) return [];
  const hits: (EdgeHit | null)[] = [];
  // Along a side two marks share (both fitted to the same line, a fraction of a pixel apart) the
  // contour would hop between them at every crossing; it stays with the polygon it is on while
  // that one has an edge close by, so the union's side is one mark's side, not a stitching of two.
  let stay = -1;
  for (let i = 0; i < n; i++) {
    const x = contour[2 * i]!,
      y = contour[2 * i + 1]!;
    let h: EdgeHit | null = null;
    if (stay >= 0) {
      const same = nearestEdge(x, y, [polys[stay]!], 0.8);
      if (same) h = { ...same, p: stay };
    }
    if (!h) h = nearestEdge(x, y, polys, maxD);
    hits.push(h);
    stay = h ? h.p : -1;
  }
  // start at a boundary between two runs, so no run is split by the contour's start
  let start = 0;
  for (let i = 0; i < n; i++) {
    const a = hits[i],
      b = hits[(i + n - 1) % n];
    if ((a?.p ?? -1) !== (b?.p ?? -1)) {
      start = i;
      break;
    }
  }
  const runs: { first: EdgeHit; last: EdgeHit; pts: number[] }[] = [];
  let i = 0;
  while (i < n) {
    const idx = (start + i) % n;
    const h = hits[idx];
    if (!h) {
      runs.push({ first: { p: -1, e: 0, t: 0, x: contour[2 * idx]!, y: contour[2 * idx + 1]! }, last: { p: -1, e: 0, t: 0, x: contour[2 * idx]!, y: contour[2 * idx + 1]! }, pts: [contour[2 * idx]!, contour[2 * idx + 1]!] });
      i++;
      continue;
    }
    const run: EdgeHit[] = [h];
    let j = i + 1;
    while (j < n) {
      const hj = hits[(start + j) % n];
      if (!hj || hj.p !== h.p) break;
      run.push(hj);
      j++;
    }
    runs.push({ first: h, last: run[run.length - 1]!, pts: emitRun(polys[h.p]!, run) });
    i = j;
  }
  // where the border passes from one polygon to the next, the two edges cross: the crossing
  // itself replaces the two nearby hits (a nook's apex, a corner where one band enters another).
  // Every boundary is settled before anything is emitted: the last run's crossing into the
  // first one is a boundary like any other
  if (runs.length > 1) {
    for (let k = 0; k < runs.length; k++) {
      const cur = runs[k]!,
        next = runs[(k + 1) % runs.length]!;
      if (cur.last.p < 0 || next.first.p < 0 || cur.last.p === next.first.p) continue;
      const X = edgeCrossing(polys[cur.last.p]!, cur.last.e, polys[next.first.p]!, next.first.e);
      if (!X || Math.hypot(X.x - cur.last.x, X.y - cur.last.y) > 8 || Math.hypot(X.x - next.first.x, X.y - next.first.y) > 8) continue;
      next.first = { ...next.first, x: X.x, y: X.y };
      next.pts[0] = X.x;
      next.pts[1] = X.y;
      // the run's own last point makes way for the crossing (a one-point run is the crossing itself)
      const pts = cur.pts;
      if (pts.length > 2) pts.length -= 2;
      else pts.length = 0;
    }
  }
  const out: number[] = [];
  for (const run of runs) for (const c of run.pts) out.push(c);
  return out;
}

/** The point where edge e of polygon a crosses edge f of polygon b (a little past the segments' ends allowed), if it does. */
function edgeCrossing(a: number[], e: number, b: number[], f: number): Vec | null {
  const na = a.length / 2,
    nb = b.length / 2;
  const ax = a[2 * e]!,
    ay = a[2 * e + 1]!,
    adx = a[2 * ((e + 1) % na)]! - ax,
    ady = a[2 * ((e + 1) % na) + 1]! - ay;
  const bx = b[2 * f]!,
    by = b[2 * f + 1]!,
    bdx = b[2 * ((f + 1) % nb)]! - bx,
    bdy = b[2 * ((f + 1) % nb) + 1]! - by;
  const den = adx * bdy - ady * bdx;
  if (Math.abs(den) < 1e-9) return null;
  const t = ((bx - ax) * bdy - (by - ay) * bdx) / den;
  const u = ((bx - ax) * ady - (by - ay) * adx) / den;
  const eps = 0.05;
  if (t < -eps || t > 1 + eps || u < -eps || u > 1 + eps) return null;
  return { x: ax + adx * t, y: ay + ady * t };
}

/** One stretch of the contour that lies along one polygon: its exact vertices between the first and last hit. */
function emitRun(poly: number[], run: EdgeHit[]): number[] {
  const out: number[] = [];
  const m = poly.length / 2;
  const first = run[0]!,
    last = run[run.length - 1]!;
  out.push(first.x, first.y);
  if (run.length === 1) return out;
  // which way along the polygon the contour travels: the net progression of edge indices
  let dir = 0;
  for (let k = 1; k < run.length; k++) {
    let d = run[k]!.e - run[k - 1]!.e;
    if (d > m / 2) d -= m;
    else if (d < -m / 2) d += m;
    if (Math.abs(d) <= 2) dir += d;
  }
  if (dir === 0) {
    for (let k = 1; k < run.length; k++) out.push(run[k]!.x, run[k]!.y);
    return out;
  }
  // how many vertices lie between the first and last hit along the way walked: the net edge
  // progression, made consistent with where the run ends (a run may circle the whole polygon)
  const fwd = dir > 0;
  const want = fwd ? (((last.e - first.e) % m) + m) % m : (((first.e - last.e) % m) + m) % m;
  const count = want + m * Math.max(0, Math.round((Math.abs(dir) - want) / m));
  let k = fwd ? (first.e + 1) % m : first.e;
  for (let i = 0; i < count; i++) {
    out.push(poly[2 * k]!, poly[2 * k + 1]!);
    k = fwd ? (k + 1) % m : (k + m - 1) % m;
  }
  out.push(last.x, last.y);
  return out;
}

/** Offset a simple polygon outward by d along vertex normals (orientation agnostic). */
export function expandOutward(pts: number[], d: number): number[] {
  const n = pts.length / 2;
  if (n < 3 || d === 0) return pts;
  let area = 0;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += pts[2 * i]! * pts[2 * j + 1]! - pts[2 * j]! * pts[2 * i + 1]!;
  }
  const sign = area > 0 ? 1 : -1;
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    const p = (i + n - 1) % n,
      q = (i + 1) % n;
    const ax = pts[2 * i]! - pts[2 * p]!,
      ay = pts[2 * i + 1]! - pts[2 * p + 1]!;
    const bx = pts[2 * q]! - pts[2 * i]!,
      by = pts[2 * q + 1]! - pts[2 * i + 1]!;
    const al = Math.hypot(ax, ay) || 1,
      bl = Math.hypot(bx, by) || 1;
    let nx = (ay / al + by / bl) * sign,
      ny = (-ax / al - bx / bl) * sign;
    const nl = Math.hypot(nx, ny) || 1;
    nx /= nl;
    ny /= nl;
    const scale = Math.min(2, 2 / Math.max(0.5, nl));
    out.push(pts[2 * i]! + nx * d * scale, pts[2 * i + 1]! + ny * d * scale);
  }
  return out;
}

function boundsOf(polys: number[][]): Rect {
  let b: Rect | null = null;
  for (const p of polys) {
    const pb = polyBounds(p);
    if (!b) b = { ...pb };
    else {
      const x = Math.min(b.x, pb.x),
        y = Math.min(b.y, pb.y);
      b = { x, y, w: Math.max(b.x + b.w, pb.x + pb.w) - x, h: Math.max(b.y + b.h, pb.y + pb.h) - y };
    }
  }
  return b ?? { x: 0, y: 0, w: 1, h: 1 };
}

/** Do two polygons share any area? */
export function polygonsOverlap(a: number[], b: number[]): boolean {
  const ba = polyBounds(a),
    bb = polyBounds(b);
  if (ba.x > bb.x + bb.w || bb.x > ba.x + ba.w || ba.y > bb.y + bb.h || bb.y > ba.y + ba.h) return false;
  const r = rasterFor(boundsOf([a, b]));
  fillPolygon(r, a, 1);
  fillPolygon(r, b, 2);
  for (let i = 0; i < r.mask.length; i++) if (r.mask[i] === 3) return true;
  return false;
}

/**
 * The border of a path stroked with round joins and flat ends (a highlighter
 * stroke): every point within width/2 of the path, cut square at both ends.
 * Built on a raster and traced, so sharp turns and loops in the path can never
 * produce spikes or self-crossings in the border.
 */
export function strokePolygon(path: number[], width: number, eps = 1): number[] {
  if (path.length < 2) return [];
  if (path.length === 2) path = [path[0]!, path[1]!, path[0]! + 0.01, path[1]!];
  const r = width / 2;
  const r2 = r * r;
  // Flat ends, by construction: the path is carried on a half width past each tip (so the
  // body reaches the tip's plane across the whole width), stroked with round joins and ends,
  // and everything beyond the plane square to the path at the tip is cut away. The direction
  // at a tip is read over a few px of path, never from one tiny last segment (a finger's last
  // sample sitting a pixel off the path would turn the cap sideways).
  const n = path.length;
  const tipX = path[n - 2]!,
    tipY = path[n - 1]!;
  let k = n - 4;
  while (k > 0 && Math.hypot(tipX - path[k]!, tipY - path[k + 1]!) < 4) k -= 2;
  let tdx = tipX - path[k]!,
    tdy = tipY - path[k + 1]!;
  let tl = Math.hypot(tdx, tdy) || 1;
  tdx /= tl;
  tdy /= tl;
  const headX = path[0]!,
    headY = path[1]!;
  k = 2;
  while (k < n - 2 && Math.hypot(path[k]! - headX, path[k + 1]! - headY) < 4) k += 2;
  let hdx = path[k]! - headX,
    hdy = path[k + 1]! - headY;
  tl = Math.hypot(hdx, hdy) || 1;
  hdx /= tl;
  hdy /= tl;
  const ext = [headX - hdx * r, headY - hdy * r, ...path, tipX + tdx * r, tipY + tdy * r];
  const reach2 = (2 * r + 2) * (2 * r + 2);
  const cut = (x: number, y: number): boolean => {
    const ex = x - tipX,
      ey = y - tipY;
    if (ex * ex + ey * ey <= reach2 && ex * tdx + ey * tdy > 0) return true;
    const sx = x - headX,
      sy = y - headY;
    return sx * sx + sy * sy <= reach2 && sx * hdx + sy * hdy < 0;
  };
  const b = polyBounds(ext);
  const cells = Math.max(1, Math.min(3, Math.round(24 / Math.max(6, width))));
  const ras = rasterFor({ x: b.x - r, y: b.y - r, w: b.w + 2 * r, h: b.h + 2 * r }, cells);
  for (let i = 0; i + 3 < ext.length; i += 2) {
    const ax = ext[i]!,
      ay = ext[i + 1]!,
      bx = ext[i + 2]!,
      by = ext[i + 3]!;
    const dx = bx - ax,
      dy = by - ay;
    const l2 = dx * dx + dy * dy || 1;
    const c0 = Math.max(0, Math.floor((Math.min(ax, bx) - r - ras.x0) * ras.s)),
      c1 = Math.min(ras.w - 1, Math.ceil((Math.max(ax, bx) + r - ras.x0) * ras.s));
    const r0 = Math.max(0, Math.floor((Math.min(ay, by) - r - ras.y0) * ras.s)),
      r1 = Math.min(ras.h - 1, Math.ceil((Math.max(ay, by) + r - ras.y0) * ras.s));
    for (let row = r0; row <= r1; row++) {
      const y = ras.y0 + (row + 0.5) / ras.s;
      for (let col = c0; col <= c1; col++) {
        const x = ras.x0 + (col + 0.5) / ras.s;
        const t = Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / l2));
        const ex = x - (ax + dx * t),
          ey = y - (ay + dy * t);
        if (ex * ex + ey * ey <= r2 && !cut(x, y)) ras.mask[row * ras.w + col] = 1;
      }
    }
  }
  const contour = traceContour(ras);
  return dedupeRun(simplify(contour, eps), 0.5);
}

/**
 * One polygon covering every input polygon (they must overlap into one blob).
 * The border is the inputs' own sides, exactly, wherever they are on the
 * outside: a merged mark still hugs the plan's lines, and a nook between two
 * marks (two banks meeting at a shallow angle) keeps its apex.
 */
export function unionPolygons(polys: number[][], eps = 0.2): number[] {
  if (polys.length === 1) return polys[0]!;
  const r = rasterFor(boundsOf(polys), 2, 16);
  for (const p of polys) fillPolygon(r, p, 1);
  // only hairline seams close (two marks that abut on one line), and only specks open away:
  // anything a person could see stays exactly as drawn
  closeRaster(r, 1);
  openRaster(r, 1);
  const contour = traceContour(r);
  const exact = rebuildOnEdges(contour, polys, 1.5 / r.s + 0.6);
  return dedupeRun(simplify(exact, eps), 0.4);
}
/**
 * What is left of a polygon once others are cut away: a mark yielding to the
 * marks of other placements. The pieces come back largest first, each rebuilt
 * from the inputs' own edges, so along a cut the seam is the other mark's side,
 * exactly, and the two abut with neither overlap nor gap. Sides a hair apart
 * (two marks fitted to the same plan line) leave no sliver: the cut is taken
 * `grow` px wider than the cutting mark and the border is put back on the marks'
 * real edges afterwards. The polygon comes back as it is (the same array) when
 * nothing a person could see overlaps; nothing comes back when it lay wholly
 * inside the cuts. Pieces smaller than `minArea` px² or thinner than `minWidth`
 * px on average are dropped; an overlap under `minOverlap` px² is no overlap.
 */
export function differencePolygons(poly: number[], cuts: number[][], minArea = 4, minWidth = 2, eps = 0.2, minOverlap = 2): number[][] {
  if (poly.length < 6) return [];
  const pb = polyBounds(poly);
  const near = cuts.filter((c) => c.length >= 6 && rectsOverlap(inflate(pb, 1), polyBounds(c)));
  if (!near.length) return [poly];
  const grow = 1.25;
  const r = rasterFor(pb, 2, 4);
  fillPolygon(r, poly, 1);
  for (const c of near) {
    fillPolygon(r, c, 2);
    fillPolygon(r, expandOutward(c, grow), 4);
  }
  const mask = r.mask;
  let removed = 0;
  for (let i = 0; i < mask.length; i++) {
    const v = mask[i]!;
    if ((v & 3) === 3) removed++;
    mask[i] = v & 4 ? 0 : v & 1;
  }
  if (removed < minOverlap * r.s * r.s) return [poly];
  closeRaster(r, 1);
  openRaster(r, 1);
  const maxD = 1.5 / r.s + 0.6 + grow;
  const out = traceContours(r, minArea * r.s * r.s)
    .map((c) => dedupeRun(simplify(rebuildOnEdges(c, [poly, ...near], maxD), eps), 0.4))
    .filter((p) => p.length >= 6 && polyArea(p) >= minArea && polyArea(p) / (polylineLength(p, true) / 2) >= minWidth);
  out.sort((a, b) => polyArea(b) - polyArea(a));
  return out;
}

/** Outer contour of every blob of set cells (8-connected), the blobs smaller than minCells left out. */
function traceContours(r: Raster, minCells: number): number[][] {
  const { w, h, mask } = r;
  const seen = new Uint8Array(w * h);
  const out: number[][] = [];
  const stack: number[] = [];
  for (let s = 0; s < mask.length; s++) {
    if (!mask[s] || seen[s]) continue;
    const cells: number[] = [];
    seen[s] = 1;
    stack.push(s);
    while (stack.length) {
      const i = stack.pop()!;
      cells.push(i);
      const x = i % w,
        y = (i - x) / w;
      for (let dy = -1; dy <= 1; dy++) {
        const ny = y + dy;
        if (ny < 0 || ny >= h) continue;
        for (let dx = -1; dx <= 1; dx++) {
          const nx = x + dx;
          if (nx < 0 || nx >= w || (!dx && !dy)) continue;
          const j = ny * w + nx;
          if (mask[j] && !seen[j]) {
            seen[j] = 1;
            stack.push(j);
          }
        }
      }
    }
    if (cells.length < minCells) continue;
    const one: Raster = { ...r, mask: new Uint8Array(w * h) };
    for (const i of cells) one.mask[i] = 1;
    const c = traceContour(one);
    if (c.length >= 6) out.push(c);
  }
  return out;
}
/** Morphological opening (erode, then dilate) with a square of half-size `r` cells: removes features thinner than 2r. */
function openRaster(ras: Raster, r: number): void {
  const eroded = morph(ras, ras.mask, r, Math.min);
  ras.mask.set(morph(ras, eroded, r, Math.max));
}
/** Morphological closing (dilate, then erode): fills gaps and notches narrower than 2r. */
function closeRaster(ras: Raster, r: number): void {
  const dilated = morph(ras, ras.mask, r, Math.max);
  ras.mask.set(morph(ras, dilated, r, Math.min));
}
/** Separable square min/max filter of half-size r. */
function morph(ras: Raster, src: Uint8Array, r: number, pick: (a: number, b: number) => number): Uint8Array {
  const { w, h } = ras;
  {
    const tmp = new Uint8Array(w * h);
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      let val = src[y * w + x]!;
      for (let k = 1; k <= r; k++) {
        if (x - k >= 0) val = pick(val, src[y * w + x - k]!);
        if (x + k < w) val = pick(val, src[y * w + x + k]!);
      }
      tmp[y * w + x] = val;
    }
    const out = new Uint8Array(w * h);
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      let val = tmp[y * w + x]!;
      for (let k = 1; k <= r; k++) {
        if (y - k >= 0) val = pick(val, tmp[(y - k) * w + x]!);
        if (y + k < h) val = pick(val, tmp[(y + k) * w + x]!);
      }
      out[y * w + x] = val;
    }
    return out;
  }
}

/** Drop consecutive polygon vertices closer than minStep (also the closing pair). */
function dedupeRun(pts: number[], minStep: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < pts.length; i += 2) {
    const m = out.length;
    if (m === 0 || Math.hypot(pts[i]! - out[m - 2]!, pts[i + 1]! - out[m - 1]!) >= minStep) out.push(pts[i]!, pts[i + 1]!);
  }
  while (out.length >= 6 && Math.hypot(out[0]! - out[out.length - 2]!, out[1]! - out[out.length - 1]!) < minStep) out.splice(out.length - 2, 2);
  return out;
}
